import React from 'react';
import { PageHeader } from '../components/ui/PageHeader';
import { Section, SectionHeading } from '../components/ui/Section';
import { Prose, ProseParagraph, ProseList } from '../components/ui/Prose';
import { PullQuote, SectionDivider } from '../components/ui/PullQuote';
import { ContinueExploring } from '../components/ui/ContinueExploring';

const WhatIsCoherenceOS = () => {
  return (
    <div>
      <PageHeader 
        title="What Is Coherence OS"
        subtitle="An operating layer for choosing what our systems create"
      />

      {/* Introduction */}
      <Section>
        <Prose>
          <ProseParagraph>
            Humanity has learned how to build systems that are extraordinarily capable. They can:
          </ProseParagraph>
          
          <ProseList items={[
            "execute at speed",
            "optimize performance",
            "scale globally",
            "adapt continuously"
          ]} />
          
          <ProseParagraph>
            What humanity has not had—until now—is a reliable way to govern the systems that generate our world. So that the world that emerges is no longer accidental, but deliberately shaped through constraints that make desired futures structurally inevitable rather than merely hoped for.
          </ProseParagraph>
          
          <ProseParagraph>
            Systems do not fail because they lack intelligence or effort. They drift because direction was never structurally governed.
          </ProseParagraph>
          
          <ProseParagraph>
            Coherence OS exists to change that.
          </ProseParagraph>
        </Prose>
      </Section>

      <SectionDivider />

      {/* A precise definition */}
      <Section>
        <SectionHeading>A precise definition</SectionHeading>
        
        <Prose>
          <ProseParagraph>
            Coherence OS is a teleology-first (purpose-governed) operating system for decision-making and execution. It enables people, teams, companies, and organizations to:
          </ProseParagraph>
          
          <ProseList items={[
            "explicitly choose the outcomes they are building toward",
            "establish non-negotiable constraints that protect dignity, life, and coherence",
            "translate that direction into measurable objectives",
            "and maintain alignment as systems grow more complex and autonomous"
          ]} />
          
          <ProseParagraph>
            Coherence OS does not replace performance frameworks like OKRs and KPIs. It governs what those frameworks are allowed to optimize for.
          </ProseParagraph>
        </Prose>
      </Section>

      <SectionDivider />

      {/* What Coherence OS governs */}
      <Section>
        <SectionHeading>What Coherence OS governs</SectionHeading>
        
        <Prose>
          <ProseParagraph>
            Coherence OS governs the layer most systems leave implicit: What kinds of success are permitted—and what kinds must be structurally impossible.
          </ProseParagraph>
          
          <ProseParagraph>
            It recognizes that certain conditions are required if systems are to contribute to a world that continues to work:
          </ProseParagraph>
          
          <ProseList items={[
            "preservation of human dignity",
            "sustainability of life-supporting systems",
            "coherence across time horizons and scale",
            "avoidance of harm displaced onto unseen others or future generations"
          ]} />
          
          <ProseParagraph>
            These are not values statements. They are conditions for continuity.
          </ProseParagraph>
        </Prose>
      </Section>

      <SectionDivider />

      {/* Discernment, precisely understood */}
      <Section>
        <SectionHeading>Discernment, precisely understood</SectionHeading>
        
        <Prose>
          <ProseParagraph>
            In Coherence OS, discernment does not mean judgment or ideology. It means recognition.
          </ProseParagraph>
          
          <ProseParagraph>
            Recognition of:
          </ProseParagraph>
          
          <ProseList items={[
            "second- and third-order consequences",
            "who bears cost versus who receives benefit",
            "whether success strengthens or weakens the future it creates"
          ]} />
          
          <ProseParagraph>
            A system can be deliberate, efficient, and profitable—and still be incoherent if it violates the conditions that allow civilization to endure.
          </ProseParagraph>
          
          <ProseParagraph>
            Coherence OS makes that distinction structural rather than reactive.
          </ProseParagraph>
        </Prose>
      </Section>

      <SectionDivider />

      {/* Purposefulness, tested by consequence */}
      <Section>
        <SectionHeading>Purposefulness, tested by consequence</SectionHeading>
        
        <Prose>
          <ProseParagraph>
            Purposefulness is not intent. It is alignment measured over time.
          </ProseParagraph>
          
          <ProseParagraph>
            A system's purpose is revealed by what its incentives and outputs consistently produce. If success:
          </ProseParagraph>
          
          <ProseList items={[
            "degrades dignity",
            "undermines trust",
            "depletes the planet",
            "or fragments coherence"
          ]} />
          
          <ProseParagraph>
            then success itself has been mis-specified.
          </ProseParagraph>
          
          <ProseParagraph>
            Coherence OS exists to prevent mis-specified success from becoming the default outcome of scale.
          </ProseParagraph>
        </Prose>
      </Section>

      <SectionDivider />

      {/* Coherence OS and performance */}
      <Section>
        <SectionHeading>Coherence OS and performance</SectionHeading>
        
        <Prose>
          <ProseParagraph>
            Coherence OS is not anti-performance. It is performance oriented toward continuity.
          </ProseParagraph>
          
          <ProseParagraph>
            It enables:
          </ProseParagraph>
          
          <ProseList items={[
            "clear objectives",
            "measurable outcomes",
            "execution velocity",
            "accountability",
            "operational excellence"
          ]} />
          
          <ProseParagraph>
            While ensuring that performance does not quietly consume the future it depends on.
          </ProseParagraph>
          
          <ProseParagraph>
            In other words, it allows systems to move fast without drifting away from what they are here to create.
          </ProseParagraph>
        </Prose>
      </Section>

      <SectionDivider />

      {/* How it functions in practice */}
      <Section>
        <SectionHeading>How it functions in practice (conceptual)</SectionHeading>
        
        <Prose>
          <ProseParagraph>
            Coherence OS operates as a governing loop across time horizons:
          </ProseParagraph>
          
          <div className="space-y-6 mt-8">
            <div>
              <h4 className="font-serif text-lg font-medium mb-2">Orient</h4>
              <ProseParagraph className="!mb-0">
                Choose direction grounded in conditions that support human and planetary continuity.
              </ProseParagraph>
            </div>
            
            <div>
              <h4 className="font-serif text-lg font-medium mb-2">Specify success</h4>
              <ProseParagraph className="!mb-0">
                Translate direction into objectives and metrics that can be executed.
              </ProseParagraph>
            </div>
            
            <div>
              <h4 className="font-serif text-lg font-medium mb-2">Constrain optimization</h4>
              <ProseParagraph className="!mb-0">
                Define boundaries that prevent speed and scale from crossing viability thresholds.
              </ProseParagraph>
            </div>
            
            <div>
              <h4 className="font-serif text-lg font-medium mb-2">Align and execute</h4>
              <ProseParagraph className="!mb-0">
                Maintain coherence across incentives, teams, systems, and time—while pursuing ambitious outcomes.
              </ProseParagraph>
            </div>
          </div>
          
          <ProseParagraph className="mt-8">
            This is how systems gain the ability to scale without losing themselves.
          </ProseParagraph>
        </Prose>
      </Section>

      <SectionDivider />

      {/* What becomes possible when optimization is governed */}
      <Section>
        <SectionHeading>What becomes possible when optimization is governed</SectionHeading>
        
        <Prose>
          <ProseParagraph>
            When direction is explicit and structurally governed, new possibilities emerge—not as aspiration, but as design:
          </ProseParagraph>
          
          <ProseList items={[
            "OKRs can be achieved without hidden harm",
            "growth can occur without dignity loss",
            "AI can scale without governance lag",
            "high performance can persist without depletion"
          ]} />
          
          <ProseParagraph>
            This is how systems become capable of contributing to:
          </ProseParagraph>
          
          <ProseList items={[
            "dignified work and participation",
            "education that develops whole humans",
            "accountable governance and AI stewardship",
            "regeneration-aligned growth and production"
          ]} />
          
          <ProseParagraph>
            Not because people try harder—but because the system is oriented correctly.
          </ProseParagraph>
        </Prose>
      </Section>

      <SectionDivider />

      {/* Protection and stewardship */}
      <Section>
        <SectionHeading>Protection and stewardship</SectionHeading>
        
        <Prose>
          <ProseParagraph>
            Coherence OS is protected by filed and pending patents covering its core architectural approach.
          </ProseParagraph>
          
          <ProseParagraph>
            This protection exists to preserve:
          </ProseParagraph>
          
          <ProseList items={[
            "the integrity of the governing layer",
            "the constraint-based boundary",
            "responsible stewardship as the category emerges"
          ]} />
        </Prose>
      </Section>

      <SectionDivider />

      {/* Who this is for */}
      <Section>
        <SectionHeading>Who this is for</SectionHeading>
        
        <Prose>
          <ProseParagraph>
            Coherence OS is for builders, leaders, and organizations who recognize:
          </ProseParagraph>
          
          <ProseList items={[
            "optimization is powerful—and dangerous without direction",
            "defaults shape civilization more than intent",
            "and the ability to choose what our systems create is now essential infrastructure"
          ]} />
          
          <ProseParagraph>
            If this framing feels obvious rather than surprising, you are already thinking at the layer Coherence OS occupies.
          </ProseParagraph>
        </Prose>
        
        {/* Continue exploring */}
        <ContinueExploring 
          links={[
            { label: 'The Category', href: '/the-category' },
            { label: 'For Investors', href: '/for-investors' },
            { label: 'For Builders', href: '/for-builders' },
          ]}
        />
      </Section>
    </div>
  );
};

export default WhatIsCoherenceOS;
